import { cn } from '@/utils/cn'
import { DIFFICULTY_LEVELS } from '@/types/reading'
import type { DifficultyLevel, Reading } from '@/types/reading'
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'

export type CefrFilter = 'all' | DifficultyLevel

interface CefrFilterBarProps {
  readings: Reading[]
  value: CefrFilter
  onChange: (filter: CefrFilter) => void
}

const CHIPS: CefrFilter[] = ['all', ...DIFFICULTY_LEVELS]

function chipLabel(filter: CefrFilter): string {
  return filter === 'all' ? 'All' : filter.toUpperCase()
}

function matchCount(readings: Reading[], filter: CefrFilter): number {
  if (filter === 'all') return readings.length
  return readings.filter((r) => r.difficulty_level === filter).length
}

/**
 * CEFR level filter (research D7). Chips are All + a0..c2 (uppercase). The active
 * chip is signalled via aria-pressed (not colour alone). On narrow viewports the bar
 * is a single horizontally-scrollable row with the label and count hidden.
 */
export function CefrFilterBar({ readings, value, onChange }: CefrFilterBarProps) {
  return (
    <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto sm:flex-wrap border-b border-line-soft pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.28em] text-muted mr-1 shrink-0">
        Level
      </span>
      {CHIPS.map((filter) => {
        const active = filter === value
        return (
          <button
            key={filter}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(filter)}
            className={cn(
              'shrink-0 min-h-11 px-3 inline-flex items-center font-mono text-xs uppercase tracking-widest rounded-full transition-colors duration-200',
              focusRing,
              active ? segmentedSelected : segmentedRest,
            )}
          >
            {chipLabel(filter)}
          </button>
        )
      })}
      <span
        aria-live="polite"
        className="hidden sm:inline ml-auto shrink-0 font-mono text-[11px] uppercase tracking-widest text-muted"
      >
        {matchCount(readings, value)} texts
      </span>
    </div>
  )
}
